/*
 * Copyright (c) 2026, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import type { ApplicationListItemResponse } from "../../api/types/swagger-api-types"

type DeviceField = "brand" | "brandName" | "model" | "modelName"

/**
 * Brand and model fields shared by device list and detail responses.
 *
 * Indexing the generated response type keeps this in sync with the API: a
 * renamed or removed field becomes a type error instead of falling back silently.
 * The API sends `null` for a name it has no value for, which the generated
 * types leave out.
 */
type DeviceIdentity = { [K in DeviceField]?: ApplicationListItemResponse[K] | null }

/**
 * Device brand and model formatted for display.
 */
export interface DeviceName {
    brand?: string | null
    model?: string | null

    /** Brand and model joined for use as an accessible label. */
    label: string
}

/**
 * Resolves the display name for a device.
 *
 * Uses the API-provided brand and model names when available and falls back to
 * the raw values for devices the API does not recognise.
 *
 * @param device - Device list or detail data.
 * @returns The display brand and model, plus both joined into a label.
 *
 * @example
 * deviceName({ brand: "samsung", brandName: "SAMSUNG", model: "SM-G950F", modelName: "Galaxy S8" })
 * // { brand: "SAMSUNG", model: "Galaxy S8", label: "SAMSUNG Galaxy S8" }
 */
export function deviceName(device: DeviceIdentity): DeviceName {
    const brand = device.brandName ?? device.brand
    const model = device.modelName ?? device.model

    return { brand, model, label: [brand, model].filter(Boolean).join(" ") }
}
